import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, X, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { clubApi } from "../lib/clubApi";
import { isSpeechSupported, startListening } from "../lib/voice";

const MAX_SECONDS = 60;
const HOLD_MS = 600;

const hasMediaRecorder = () =>
    typeof window !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);

const errMsg = (e) => {
    if (e?.response?.status === 422) return "Registrazione vuota: non ho sentito nulla. Riprova.";
    const d = e?.response?.data?.detail;
    if (typeof d === "string" && d) return d;
    return e?.message || "Errore";
};

const pickMime = () => {
    const c = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/ogg"];
    return c.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
};

// Records audio in the browser (MediaRecorder) and sends it to Gemini for transcription + parsing.
// Hold the button to talk, or tap once to start and tap again to stop.
export default function VoiceMemberModal({ open, onClose, onCaptured }) {
    const [recording, setRecording] = useState(false);
    const [toggleMode, setToggleMode] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [liveText, setLiveText] = useState("");

    const recRef = useRef(null);
    const wantRef = useRef(false);
    const pressAtRef = useRef(0);
    const ignoreUpRef = useRef(false);
    const timerRef = useRef(null);
    const speechTextRef = useRef("");

    const useMedia = hasMediaRecorder();
    const supported = useMedia || isSpeechSupported();

    const cleanup = () => {
        wantRef.current = false;
        clearInterval(timerRef.current);
        const r = recRef.current;
        recRef.current = null;
        if (r?.recorder && r.recorder.state !== "inactive") { try { r.recorder.onstop = null; r.recorder.stop(); } catch {} }
        r?.stream?.getTracks().forEach((t) => t.stop());
        try { r?.ctrl?.stop(); } catch {}
    };

    useEffect(() => {
        if (!open) {
            cleanup();
            setRecording(false); setToggleMode(false); setSeconds(0);
            setProcessing(false); setError(""); setResult(null); setLiveText("");
        }
        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const startTimer = () => {
        clearInterval(timerRef.current);
        const t0 = Date.now();
        timerRef.current = setInterval(() => {
            const s = Math.floor((Date.now() - t0) / 1000);
            setSeconds(s);
            if (s >= MAX_SECONDS) stopRec();
        }, 250);
    };

    const startRec = async () => {
        setError(""); setResult(null); setLiveText(""); setSeconds(0);
        speechTextRef.current = "";
        if (useMedia) {
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch {
                wantRef.current = false;
                setRecording(false); setToggleMode(false);
                setError("Microfono bloccato. Concedi il permesso al microfono e riprova.");
                return;
            }
            if (!wantRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
            const mime = pickMime();
            const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
            recorder.onstop = () => {
                stream.getTracks().forEach((t) => t.stop());
                processAudio(new Blob(chunks, { type: recorder.mimeType || mime || "audio/webm" }));
            };
            recorder.start(250);
            recRef.current = { recorder, stream };
        } else {
            const ctrl = startListening({
                lang: "it-IT",
                continuous: true,
                onResult: (text) => { speechTextRef.current = text; setLiveText(text); },
                onError: (err) => {
                    if (err === "not-allowed") setError("Microfono bloccato. Concedi il permesso e riprova.");
                    else if (err !== "aborted" && err !== "no-speech") setError(`Errore: ${err}`);
                },
            });
            recRef.current = { ctrl };
        }
        startTimer();
    };

    const stopRec = () => {
        if (!wantRef.current) return;
        wantRef.current = false;
        clearInterval(timerRef.current);
        setRecording(false); setToggleMode(false);
        const r = recRef.current;
        recRef.current = null;
        if (!r) return;
        if (r.recorder) {
            if (r.recorder.state !== "inactive") r.recorder.stop();
            else r.stream?.getTracks().forEach((t) => t.stop());
        } else if (r.ctrl) {
            try { r.ctrl.stop(); } catch {}
            setTimeout(() => {
                const text = (speechTextRef.current || "").trim();
                if (text) processText(text);
                else setError("Non ho sentito nulla. Tieni premuto e parla più forte.");
            }, 250);
        }
    };

    const onDown = (e) => {
        try { e.preventDefault(); } catch {}
        if (!supported) { setError("Il tuo browser non supporta il microfono. Usa Chrome o Safari."); return; }
        if (processing) return;
        if (wantRef.current) { ignoreUpRef.current = true; stopRec(); return; }
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
        pressAtRef.current = Date.now();
        wantRef.current = true;
        setRecording(true); setToggleMode(false);
        startRec();
    };

    const onUp = (e) => {
        try { e.preventDefault(); } catch {}
        if (ignoreUpRef.current) { ignoreUpRef.current = false; return; }
        if (!wantRef.current) return;
        const held = Date.now() - pressAtRef.current;
        if (held < HOLD_MS || !recRef.current) setToggleMode(true);
        else stopRec();
    };

    const onCancel = () => { if (wantRef.current && recRef.current) setToggleMode(true); };

    const processAudio = async (blob) => {
        if (!blob || blob.size < 1000) {
            setError("Non ho sentito nulla. Tieni premuto più a lungo mentre parli.");
            return;
        }
        setProcessing(true); setError("");
        try {
            const r = await clubApi.parseVoiceMemberAudio(blob);
            setResult(r);
            if (r.transcript) setLiveText(r.transcript);
            if (!r.name && !r.phone && !r.address && !r.zone) {
                setError(r.summary || "Non ho capito nessun campo. Riprova parlando più chiaramente.");
            }
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setProcessing(false);
        }
    };

    const processText = async (text) => {
        setProcessing(true); setError("");
        try {
            const r = await clubApi.parseVoiceMember(text);
            setResult(r);
            if (!r.name && !r.phone && !r.address && !r.zone) {
                setError(r.summary || "Non ho capito nessun campo. Riprova parlando più chiaramente.");
            }
        } catch (e) {
            setError(errMsg(e));
        } finally {
            setProcessing(false);
        }
    };

    const apply = () => {
        if (!result) return;
        onCaptured?.({
            name: result.name || "",
            phone: result.phone || "",
            address: result.address || "",
            zone: result.zone || "",
        });
        toast.success("Dati inseriti dalla voce");
        onClose?.();
    };

    const hasFields = result && (result.name || result.phone || result.address || result.zone);

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            role="dialog"
            data-testid="voice-member-modal"
            onClick={(e) => e.target === e.currentTarget && !recording && onClose?.()}
        >
            <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-stone-100">
                    <div className="flex items-center gap-2">
                        <Sparkles size={18} className="text-stone-900" />
                        <h3 className="font-display text-lg text-stone-900">Aggiungi socio con la voce</h3>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-stone-100 text-stone-500" aria-label="Chiudi" data-testid="voice-member-close">
                        <X size={20} />
                    </button>
                </div>

                <div className="px-5 pb-6 pt-4">
                    {!supported && (
                        <div className="rounded-xl bg-red-50 text-red-700 p-3 text-sm mb-3">
                            Il tuo browser non supporta il microfono. Usa Chrome o Safari.
                        </div>
                    )}

                    <div className="flex flex-col items-center py-3 select-none">
                        <button
                            type="button"
                            onPointerDown={onDown}
                            onPointerUp={onUp}
                            onPointerCancel={onCancel}
                            onContextMenu={(e) => e.preventDefault()}
                            disabled={!supported || processing}
                            className={`relative w-28 h-28 rounded-full grid place-items-center transition-all shadow-lg select-none touch-none ${
                                recording ? "bg-red-500 text-white scale-110" : "bg-stone-900 hover:bg-stone-800 text-white"
                            } disabled:opacity-50`}
                            style={{ WebkitUserSelect: "none", WebkitTouchCallout: "none" }}
                            data-testid="voice-member-mic"
                            data-recording={recording ? "1" : "0"}
                            aria-label={recording ? "Sto registrando" : "Tieni premuto per registrare"}
                        >
                            {recording && toggleMode ? <Square size={38} fill="currentColor" /> : <Mic size={42} />}
                            {recording && (
                                <>
                                    <span className="absolute inset-0 rounded-full ring-4 ring-red-300 animate-ping" />
                                    <span className="absolute -bottom-2 -right-2 bg-white text-red-500 text-[10px] font-bold px-2 py-0.5 rounded-full shadow tabular-nums">
                                        REC {String(Math.floor(seconds / 60)).padStart(1, "0")}:{String(seconds % 60).padStart(2, "0")}
                                    </span>
                                </>
                            )}
                        </button>
                        <p className="mt-4 text-sm font-bold text-stone-800 text-center" data-testid="voice-member-status">
                            {recording
                                ? (toggleMode ? "🔴 Sto ascoltando… tocca per fermare" : "🔴 Sto ascoltando… continua a parlare")
                                : processing
                                    ? "Sto trascrivendo e analizzando…"
                                    : "Tieni premuto e parla (o tocca una volta)"}
                        </p>
                        <p className="mt-1 text-xs text-stone-500 text-center max-w-xs leading-snug">
                            {recording
                                ? (toggleMode ? "Quando il cliente ha finito, tocca il pulsante rosso." : "Rilascia il pulsante quando hai finito.")
                                : "Passa il telefono al cliente. Es. \"Mi chiamo Mario Rossi, abito in Via Roma 12 a Pegli, il mio numero è 333 1234567\"."}
                        </p>
                    </div>

                    {liveText && (
                        <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 mb-3" data-testid="voice-member-transcript">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1">Il cliente ha detto:</p>
                            <p className="text-sm text-stone-700">{liveText}</p>
                        </div>
                    )}

                    {processing && (
                        <div className="flex items-center gap-2 text-sm text-stone-600 justify-center py-2">
                            <Loader2 size={16} className="animate-spin" /> Sto interpretando…
                        </div>
                    )}

                    {error && (
                        <div className="rounded-xl bg-red-50 text-red-700 p-3 text-sm" data-testid="voice-member-error">
                            {error}
                        </div>
                    )}

                    {hasFields && (
                        <div className="space-y-2 mt-2" data-testid="voice-member-result">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Ho capito</p>
                            <ul className="rounded-xl border border-stone-200 divide-y divide-stone-200 overflow-hidden">
                                {[
                                    ["Nome", result.name],
                                    ["Telefono", result.phone],
                                    ["Indirizzo", result.address],
                                    ["Zona", result.zone],
                                ].map(([label, val]) => (
                                    <li key={label} className="flex items-start gap-3 px-3 py-2 text-sm bg-white">
                                        <span className="text-[11px] uppercase font-bold text-stone-500 w-20 shrink-0 pt-0.5">{label}</span>
                                        <span className="flex-1 text-stone-900 break-words">{val || <em className="text-stone-400">non detto</em>}</span>
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={apply}
                                className="w-full py-3 rounded-2xl bg-stone-900 text-white font-bold shadow-md active:scale-[0.99] inline-flex items-center justify-center gap-2"
                                data-testid="voice-member-apply"
                            >
                                <Check size={16} /> Usa questi dati
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
